<template>
    <div class="headContainer">
        <!-- 移动端菜单按钮（控制侧边栏） -->
        <div class="menu-toggle" v-if="isMobile" @click="toggleSidebar">
            <i class="el-icon-menu"></i>
        </div>

        <!-- 左侧当前菜单标题：移动端适配换行和字体 -->
        <div class="menu-title" :class="{ 'mobile-title': isMobile }">
            {{ activeMenu?.meta?.title || '优优洗车管理系统' }}
        </div>

        <!-- 右侧用户信息：移动端优化布局和尺寸 -->
        <div class="user-info">
            <!-- 头像点击显示用户信息弹窗 -->
            <el-avatar
                size="large"
                :src="userInfo.avatar || defaultAvatar"
                class="user-avatar"
                @click="showUserDialog = true"
            ></el-avatar>

            <!-- 用户名：移动端可配置隐藏 -->
            <span class="user-name" v-show="!hideUserName">
                {{ userInfo.userName || '管理员' }}
            </span>

            <!-- 退出按钮：优化交互 -->
            <span class="logOut" @click="handleLogOut">
                <i class="el-icon-switch-button"></i>
                <span class="logout-text" v-show="!hideLogoutText">退出</span>
            </span>
        </div>

        <!-- 用户信息弹窗 -->
        <el-dialog
            v-model="showUserDialog"
            title="个人信息"
            width="80%"
            center
        >
            <div class="user-dialog-content">
                <el-avatar
                    :src="userInfo.avatar || defaultAvatar"
                    size="large"
                    class="dialog-avatar"
                ></el-avatar>
                <div class="user-detail">
                    <p><span class="label">用户名：</span>{{ userInfo.userName || '未设置' }}</p>
                    <p><span class="label">角色：</span>{{ userInfo.roleName || '普通管理员' }}</p>
                    <p><span class="label">登录时间：</span>{{ formatTime(userInfo.loginTime) || '未知' }}</p>
                </div>
            </div>
            <template #footer>
                <el-button @click="showUserDialog = false">关闭</el-button>
            </template>
        </el-dialog>
    </div>
</template>

<script>
import { computed, inject, ref, onMounted, onBeforeUnmount, watch } from "vue";
import { useStore } from "vuex";
import { useRoute, useRouter } from "vue-router";
import { ElMessage, ElMessageBox, ElDialog } from "element-plus";
import { apiLogout } from "@/api/requestApis.js";

export default {
    components: { ElDialog },
    setup() {
        const store = useStore();
        const router = useRouter();
        const route = useRoute();

        // 注入侧边栏切换方法（从父容器传递）
        const toggleSidebar = inject("toggleAside", () => {});

        // 响应式状态
        const userInfo = inject("userInfo", ref({}));
        const isMobile = ref(true);
        const showUserDialog = ref(false);
        const defaultAvatar = ref("https://cube.elemecdn.com/3/7c/3ea6beec64369c2642b92c6726f1epng.png");

        // 移动端配置项（可根据需求调整）
        const hideUserName = ref(true);    // 移动端是否隐藏用户名
        const hideLogoutText = ref(true);  // 移动端是否隐藏退出文字

        // 当前激活菜单
        const activeMenu = computed(() => {
            return route.matched.find(item => item.meta?.title) || {};
        });

        // 格式化时间
        const formatTime = (timeStr) => {
            if (!timeStr) return '';
            try {
                const date = new Date(timeStr);
                return `${date.getFullYear()}-${(date.getMonth()+1).toString().padStart(2, '0')}-${date.getDate().toString().padStart(2, '0')} ${date.getHours().toString().padStart(2, '0')}:${date.getMinutes().toString().padStart(2, '0')}`;
            } catch (error) {
                return timeStr;
            }
        };

        // 退出登录（增加确认弹窗）
        const handleLogOut = async () => {
            try {
                const confirm = await ElMessageBox.confirm(
                    '确定要退出登录吗？',
                    '温馨提示',
                    {
                        confirmButtonText: '确定',
                        cancelButtonText: '取消',
                        type: 'warning',
                        center: true
                    }
                );

                if (confirm) {
                    await apiLogout();
                    store.dispatch("clearUserMenu");
                    localStorage.clear();

                    ElMessage.success('退出成功');
                    router.push({ name: "login" });
                }
            } catch (error) {
                if (error !== 'cancel') { // 排除取消操作
                    console.error("退出登录失败：", error);
                    ElMessage.error("退出登录失败，请重试");
                }
            }
        };

        // 检测屏幕尺寸
        const checkScreenSize = () => {
            const width = window.innerWidth;
            isMobile.value = width < 768;

            // 移动端配置
            if (isMobile.value) {
                hideUserName.value = width < 480;    // 480px以下隐藏用户名
                hideLogoutText.value = width < 400;  // 400px以下隐藏退出文字
            } else {
                hideUserName.value = false;
                hideLogoutText.value = false;
            }
        };

        // 监听路由变化，更新激活菜单
        watch(
            () => route.path,
            () => {
                // 路由变化时关闭用户弹窗
                showUserDialog.value = false;
            },
            { immediate: true }
        );

        // 生命周期
        onMounted(() => {
            checkScreenSize();
            window.addEventListener("resize", checkScreenSize);
        });

        onBeforeUnmount(() => {
            window.removeEventListener("resize", checkScreenSize);
        });

        return {
            activeMenu,
            handleLogOut,
            userInfo,
            defaultAvatar,
            isMobile,
            showUserDialog,
            hideUserName,
            hideLogoutText,
            toggleSidebar,
            formatTime
        };
    },
};
</script>

<style scoped>
/* 基础容器样式 */
.headContainer {
    display: flex;
    align-items: center;
    justify-content: space-between;
    height: 100%;
    box-sizing: border-box;
    padding: 0 16px;
    background-color: #fff;
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.1);
    position: relative;
    z-index: 10;
}

/* 移动端菜单按钮 */
.menu-toggle {
    width: 40px;
    height: 40px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    background-color: rgba(0, 0, 0, 0.05);
    cursor: pointer;
    margin-right: 12px;
    color: #666;
    transition: all 0.2s ease;
}

.menu-toggle:hover {
    background-color: rgba(0, 0, 0, 0.1);
    color: #333;
}

/* 左侧菜单标题 */
.menu-title {
    font-size: 18px;
    font-weight: 500;
    color: #333;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 60%;
    transition: all 0.2s ease;
}

/* 右侧用户信息容器 */
.user-info {
    display: flex;
    align-items: center;
    gap: 8px;
}

/* 用户头像 */
.user-avatar {
    cursor: pointer;
    transition: transform 0.2s ease;
    position: relative;
}

.user-avatar:hover {
    transform: scale(1.05);
}

/* 用户名 */
.user-name {
    font-size: 14px;
    color: #666;
    white-space: nowrap;
    transition: all 0.2s ease;
}

/* 退出按钮 */
.logOut {
    display: flex;
    align-items: center;
    gap: 4px;
    cursor: pointer;
    font-size: 14px;
    color: #f56c6c;
    padding: 6px 10px;
    border-radius: 4px;
    transition: all 0.2s ease;
    white-space: nowrap;
}

.logOut:hover {
    background-color: #fef0f0;
    transform: translateY(-1px);
}

/* 用户信息弹窗样式 */
.user-dialog-content {
    display: flex;
    align-items: center;
    gap: 20px;
    padding: 10px 0;
}

.dialog-avatar {
    flex-shrink: 0;
}

.user-detail {
    flex: 1;
}

.user-detail .label {
    color: #999;
    font-size: 14px;
    margin-right: 8px;
}

.user-detail p {
    line-height: 1.8;
    margin: 0;
    font-size: 15px;
}

/* 移动端适配 (768px以下) */
@media screen and (max-width: 767px) {
    .headContainer {
        padding: 0 12px;
        height: 60px;
    }

    .menu-title {
        font-size: 16px;
        max-width: calc(100% - 120px);
    }

    /* 头像尺寸优化 */
    ::v-deep .user-avatar .el-avatar {
        width: 36px !important;
        height: 36px !important;
        font-size: 16px !important;
    }

    .user-name {
        font-size: 12px;
    }

    .logOut {
        padding: 4px 8px;
        font-size: 12px;
    }

    /* 弹窗适配 */
    ::v-deep .el-dialog {
        border-radius: 8px;
    }

    .user-dialog-content {
        flex-direction: column;
        gap: 15px;
        text-align: center;
    }
}

/* 小屏手机适配 (480px以下) */
@media screen and (max-width: 479px) {
    .menu-title {
        font-size: 15px;
        max-width: calc(100% - 100px);
    }

    ::v-deep .user-avatar .el-avatar {
        width: 32px !important;
        height: 32px !important;
        font-size: 14px !important;
    }

    .logOut {
        padding: 2px 6px;
    }
}

/* 平板适配 (768px - 1024px) */
@media screen and (min-width: 768px) and (max-width: 1024px) {
    .headContainer {
        padding: 0 14px;
        height: 70px;
    }

    .menu-title {
        font-size: 17px;
        max-width: 55%;
    }

    ::v-deep .user-avatar .el-avatar {
        width: 40px !important;
        height: 40px !important;
        font-size: 18px !important;
    }

    .user-name {
        font-size: 13px;
    }

    .logOut {
        padding: 5px 9px;
        font-size: 13px;
    }
}

/* 大屏样式 (1025px以上) */
@media screen and (min-width: 1025px) {
    .headContainer {
        padding: 0 20px;
        height: 80px;
    }

    .menu-title {
        font-size: 19px;
    }

    ::v-deep .user-avatar .el-avatar {
        width: 44px !important;
        height: 44px !important;
        font-size: 20px !important;
    }

    /* 隐藏移动端菜单按钮 */
    .menu-toggle {
        display: none;
    }
}

/* 通用优化 */
::v-deep .el-avatar {
    --el-avatar-bg-color: #f0f2f5;
}

/* 禁止选中文本 */
.headContainer {
    user-select: none;
    -webkit-user-select: none;
}

/* 暗黑模式适配 */
@media (prefers-color-scheme: dark) {
    .headContainer {
        background-color: rgb(45, 58, 75);
        box-shadow: 0 1px 4px rgba(0, 0, 0, 0.3);
    }

    .menu-title {
        color: #fff;
    }

    .menu-toggle {
        background-color: rgba(255, 255, 255, 0.1);
        color: #e0e0e0;
    }

    .menu-toggle:hover {
        background-color: rgba(255, 255, 255, 0.2);
    }

    .user-name {
        color: #e0e0e0;
    }

    .logOut {
        color: #ff8a80;
    }

    .logOut:hover {
        background-color: #333;
    }

    .user-detail .label {
        color: #bbb;
    }
}

/* 动画优化 */
::v-deep .el-dialog {
    animation: dialogFadeIn 0.2s ease;
}

@keyframes dialogFadeIn {
    from {
        opacity: 0;
        transform: translateY(-20px);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
}
</style>
